import { useMemo, useState } from 'react'

import { RecordType } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import { NodePageDocument } from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import {
  NODE_UUID_TYPES,
  findNodeByUuid,
  formatUuidToNodeType,
  getAllSalariesFromChildrenAttributes,
  getChildNodeType,
  getNodeType,
  getSalariesFromAttributes,
  parseTreeNodes,
} from 'modules/job_catalog/lib/nodesUtils'
import { Salary, Workplace } from 'modules/job_catalog/lib/salary'
import { groupSalariesByCurrency, summarizeSalaries } from 'modules/job_catalog/lib/salaryUtils'

import { useSalaryActions } from './useSalaryActions'

export type SalaryRecord = RecordType & Salary

export const useSalaryPage = (nodeUuid: string) => {
  const nodeType = useMemo(() => getNodeType(nodeUuid), [nodeUuid])
  const childType = getChildNodeType(nodeUuid)
  const isLevel = useMemo(() => nodeType === NODE_UUID_TYPES.LEVEL, [nodeType])

  const [nodeName, setNodeName] = useState<string>('')
  const [showSalaryModal, setShowSalaryModal] = useState(false)
  const [editSalary, setEditedSalary] = useState<Salary>()
  const [salaries, setSalaries] = useState<Salary[]>([])
  const [selectedWorkplaces, setSelectedWorkplaces] = useState<Workplace[]>([])

  const { handleCreateSalary, handleUpdateSalary, handleDeleteSalary } = useSalaryActions()

  const source = useGraphqlDataCollectionSource(
    {
      document: NodePageDocument,
      mapData: (data) => {
        const treeNodes = parseTreeNodes(data.jobCatalog.treesConnection.nodes[0])
        const foundNode = findNodeByUuid(treeNodes, nodeUuid)
        if (foundNode) setNodeName(foundNode.name)
        let salaryAttributes: Salary[] = []
        if (isLevel) {
          const nodeAttributes = data?.jobCatalog.nodeAttributesConnection.nodes || []
          salaryAttributes = getSalariesFromAttributes(nodeAttributes)
        } else {
          salaryAttributes = mergeSalariesByWorkplaceAndCurrency(
            getAllSalariesFromChildrenAttributes(data?.jobCatalog.treeNodesConnection.nodes || [])
          )
        }
        setSalaries(salaryAttributes)
        setSelectedWorkplaces(getWorkplacesFromSalaries(salaryAttributes))
        return salaryAttributes.map((salary) => ({ ...salary }))
      },
      ...(isLevel && {
        primaryActions: () => ({
          label: i18n.t('job_catalog.nodes.actions.new'),
          onClick: () => {
            setEditedSalary(undefined)
            setShowSalaryModal(true)
          },
          icon: Plus,
        }),
        itemActions: (salary) => [
          {
            label: i18n.t('job_catalog.nodes.salaries.edit'),
            onClick: async () => {
              if (!recordIsSalary(salary)) return
              setEditedSalary(salary)
              setShowSalaryModal(true)
            },
          },
          {
            label: i18n.t('job_catalog.nodes.salaries.delete'),
            onClick: async () => {
              if (!recordIsSalary(salary)) return
              await handleDeleteSalary(salary, nodeUuid)
            },
          },
        ],
      }),
      ...(!isLevel && {
        grouping: {
          mandatory: true,
          collapsible: true,
          groupBy: {
            nodeId: {
              name: i18n.t('job_catalog.nodes.salaries.groupBy'),
              label: (nodeId) => salaries.find((salary) => salary.nodeId == nodeId)?.nodeName || '',
              itemCount: (nodeId) => salaries.filter((salary) => salary.nodeId == nodeId).length,
            },
          },
        },
      }),
      mapPagination: () => ({
        total: salaries.length ?? 0,
      }),

      mapVariables: ({ search }) => ({
        search,
        nodeId: Number(nodeUuid?.split('-')[1]),
        nodeType: formatUuidToNodeType(nodeUuid || ''),
        childType: formatUuidToNodeType(childType || ''),
      }),
    },
    []
  )

  const hasAllWorkplacesSalary = useMemo(() => {
    return salaries.some((salary) => !salary.workplaces || salary.workplaces.length === 0)
  }, [salaries])

  return {
    source,
    nodeName,
    nodeType,
    selectedWorkplaces,
    hasAllWorkplacesSalary,
    showSalaryModal,
    setShowSalaryModal,
    editSalary,
    handleCreateSalary,
    handleUpdateSalary,
    handleDeleteSalary,
  }
}

export const recordIsSalary = (record: RecordType): record is SalaryRecord =>
  record.id !== undefined

const mergeSalariesByWorkplaceAndCurrency = (salaries: Salary[]): Salary[] => {
  let mergedSalaries: Salary[] = []
  const groupedByNodeSalaries = groupBy(salaries, 'nodeId')
  Object.values(groupedByNodeSalaries).forEach((nodeGroupedSalaries: Salary[]) => {
    Object.values(groupSalariesByCurrency(nodeGroupedSalaries)).forEach(
      (currencyGroupedSalaries: Salary[]) => {
        const salariesWithWorkplaces = currencyGroupedSalaries.filter(
          (salary) => salary.workplaces && salary.workplaces.length > 0
        )
        const salariesWithAllWorkplaces = currencyGroupedSalaries.filter(
          (salary) => salary.workplaces?.length === 0
        )
        const salaryAllWorkplaces = summarizeSalaries(salariesWithAllWorkplaces)
        mergedSalaries = [...mergedSalaries, ...salariesWithWorkplaces]
        if (salaryAllWorkplaces) mergedSalaries.push(salaryAllWorkplaces)
      }
    )
  })
  return mergedSalaries
}

const getWorkplacesFromSalaries = (salaries: Salary[]) => {
  const workplaces = new Set<Workplace>()
  salaries.forEach((salary) => {
    salary.workplaces?.forEach((workplace) => workplaces.add(workplace))
  })
  return Array.from(workplaces)
}
